import React, { useState, useRef, useEffect } from "react";
import "./EventManagement.css";
import useAxios from "../utils/useAxios";
import { useAppContext } from "../context/AppContext";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const EMPTY_FORM = { name: "", timing: "", description: "", image: null };

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

function toDateKey(dateObj) {
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, "0");
  const d = String(dateObj.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatDisplay(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const EventManagement = () => {
  const [events, setEvents] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateEvents, setSelectedDateEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [deletingEventId, setDeletingEventId] = useState(null);

  const fileInputRef = useRef(null);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const axiosAPI = useAxios();
  const { state } = useAppContext();
  const isAdmin = state?.user?.role === "admin";

  //   console.log("isAdmin:", isAdmin);

  /* ─── helpers ─────────────────────────────────────────────── */

  const getEventsForDate = (dateKey) =>
    events.filter((e) => e?.event_datetime?.startsWith(dateKey));

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = "Event name is required.";
    if (!formData.timing) e.timing = "Timing is required.";
    if (!formData.description.trim())
      e.description = "Description is required.";
    if (!formData.image) e.image = "Please upload an image.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ─── handlers ────────────────────────────────────────────── */

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Revoke previous object URL to avoid memory leaks
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    const url = URL.createObjectURL(file);
    setFormData((prev) => ({ ...prev, image: file }));
    setImagePreview(url);
    setErrors((prev) => ({ ...prev, image: "" }));
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);

    try {
      // FormData object for multipart/form-data
      const data = new FormData();
      data.append("name", formData.name.trim());
      data.append("timing", formData.timing); // ISO string
      data.append("description", formData.description.trim());
      data.append("image", formData.image); // File object

      const res = await axiosAPI.post("/api/event/add", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const newEvent = res.data.event; // Use backend response
      setEvents((prev) => [...prev, newEvent]);

      // Reset
      setFormData(EMPTY_FORM);
      setImagePreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      window.notifyMessage("Success", "Event added successfully!", "success");
      setLoading(false);
      // Auto-select the event's date on the calendar
      const eventDateKey = (newEvent.event_datetime || newEvent.timing).slice(
        0,
        10,
      );
      setSelectedDate(eventDateKey);
      setSelectedDateEvents((prev) => [...prev, newEvent]);
    } catch (error) {
      console.error("Error adding event:", error);
      window.notifyMessage(
        "Error",
        error?.response?.data?.message ||
          "Failed to add event. Please try again.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const getAllEvents = async () => {
    try {
      const res = await axiosAPI.get("/api/event/get-all");
      setEvents(res.data.events);
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };

  const handlePrevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));

  const handleNextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));

  async function handleDateClick(dateKey) {
    try {
      const res = await axiosAPI.get(`/api/event/get-by-date?date=${dateKey}`);
      const eventsOnDate = Array.isArray(res.data.events)
        ? res.data.events
        : [];

        // console.log("Response",res)

      setSelectedDate(dateKey);
      setSelectedDateEvents(eventsOnDate);
    } catch (error) {
      console.log("Error handling date click:", error);
      setSelectedDate(dateKey);
      setSelectedDateEvents([]);
      window.notifyMessage(
        "Error",
        error?.response?.data?.message ||
          "Failed to handle date click. Please try again.",
        "error",
      );
    }
  }

  const handleDeleteEvent = async (event) => {
    if (!isAdmin) return;

    // console.log("Attempting to delete event:", event);

    if (!event?.id) {
      window.notifyMessage(
        "Error",
        "This event does not have an id, so it cannot be deleted.",
        "error",
      );
      return;
    }

    const isConfirmed = window.confirm(
      `Are you sure you want to delete "${event.name}"?`,
    );

    if (!isConfirmed) return;

    // console.log("Deleting event with id:", event.id);
    // return;

    setDeletingEventId(event.id);

    try {
      await axiosAPI.delete(`/api/event/delete/${event.id}`);

      setEvents((prev) => prev.filter((e) => e.id !== event.id));
      setSelectedDateEvents((prev) => prev.filter((e) => e.id !== event.id));

      if (selectedEvent?.id === event.id) {
        setSelectedEvent(null);
      }

      window.notifyMessage("Success", "Event deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting event:", error);
      window.notifyMessage(
        "Error",
        error?.response?.data?.message ||
          "Failed to delete event. Please try again.",
        "error",
      );
    } finally {
      setDeletingEventId(null);
    }
  };

  /* ─── calendar rendering ──────────────────────────────────── */

  const renderCalendar = () => {
    const cells = [];
    const totalDays = getDaysInMonth(year, month);
    const startDay = getFirstDayOfMonth(year, month);
    const todayKey = toDateKey(new Date());

    for (let i = 0; i < startDay; i++) {
      cells.push(
        <div key={`empty-${i}`} className="em-cal-day em-cal-day--empty" />,
      );
    }

    for (let d = 1; d <= totalDays; d++) {
      const dateKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const dayEvents = getEventsForDate(dateKey);
      const isToday = dateKey === todayKey;
      const isSelected = dateKey === selectedDate;

      cells.push(
        <div
          key={dateKey}
          className={[
            "em-cal-day",
            isToday ? "em-cal-day--today" : "",
            isSelected ? "em-cal-day--selected" : "",
            dayEvents.length > 0 ? "em-cal-day--has-events" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          //   onClick={() => setSelectedDate(dateKey)}
          onClick={() => handleDateClick(dateKey)}
          title={
            dayEvents.length > 0 ? `${dayEvents.length} event(s)` : undefined
          }
        >
          <span className="em-day-num">{d}</span>
          {dayEvents.length > 0 && (
            <div className="em-dots">
              {dayEvents.slice(0, 3).map((ev) => (
                <span key={ev.id} className="em-dot" />
              ))}
            </div>
          )}
        </div>,
      );
    }

    return cells;
  };

  /* ─── render ──────────────────────────────────────────────── */

  useEffect(() => {
    getAllEvents();
  }, []);

  return (
    <div className="container-fluid em-theme">
      <div className="page-inner">
        {/* ── Page Header ── */}
        <div className="page-header">
          <h3 className="fw-bold mb-1">Event Management</h3>
        </div>

        <div className="row g-4">
          {/* ══════ LEFT — CALENDAR ══════ */}
          <div className="col-xl-7 col-lg-12">
            <div
              className="card border-0 shadow-sm h-100"
              style={{ borderRadius: 18 }}
            >
              {/* Gradient calendar header */}
              <div
                className="em-gradient-purple d-flex justify-content-between align-items-center px-4 py-3"
                style={{ borderRadius: "18px 18px 0 0" }}
              >
                <button className="em-nav-btn" onClick={handlePrevMonth}>
                  <i
                    className="fas fa-chevron-left"
                    style={{ fontSize: "0.8rem" }}
                  />
                </button>
                <div className="text-center">
                  <div
                    className="text-white fw-bold"
                    style={{ fontSize: "1.1rem", letterSpacing: "0.02em" }}
                  >
                    {MONTH_NAMES[month]}
                  </div>
                  <div
                    className="text-white"
                    style={{ fontSize: "0.75rem", opacity: 0.75 }}
                  >
                    {year}
                  </div>
                </div>
                <button className="em-nav-btn" onClick={handleNextMonth}>
                  <i
                    className="fas fa-chevron-right"
                    style={{ fontSize: "0.8rem" }}
                  />
                </button>
              </div>

              <div className="card-body px-3 pb-4 pt-3">
                {/* Calendar grid */}
                <div className="em-cal-grid mb-2">
                  {DAY_NAMES.map((d) => (
                    <div key={d} className="em-day-label">
                      {d}
                    </div>
                  ))}
                  {renderCalendar()}
                </div>

                {/* Legend */}
                <div className="d-flex align-items-center gap-3 mt-1 mb-3 px-1">
                  <div className="d-flex align-items-center gap-1">
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: "linear-gradient(135deg,#16202b,#30465e)",
                        display: "inline-block",
                      }}
                    />
                    <span className="text-muted" style={{ fontSize: "0.7rem" }}>
                      Selected
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: "linear-gradient(135deg,#fff8ee,#f3e2c7)",
                        border: "1.5px solid #d2a268",
                        display: "inline-block",
                      }}
                    />
                    <span className="text-muted" style={{ fontSize: "0.7rem" }}>
                      Today
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <span className="em-dot" style={{ flexShrink: 0 }} />
                    <span className="text-muted" style={{ fontSize: "0.7rem" }}>
                      Has events
                    </span>
                  </div>
                </div>

                <hr className="mt-0 mb-3" />

                {/* Selected date events */}
                {selectedDate ? (
                  <div>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div>
                        <div
                          className="fw-bold text-dark"
                          style={{ fontSize: "0.95rem" }}
                        >
                          {new Date(
                            selectedDate + "T00:00:00",
                          ).toLocaleDateString("en-IN", {
                            weekday: "long",
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })}
                        </div>
                        <div
                          className="text-muted"
                          style={{ fontSize: "0.75rem" }}
                        >
                          {selectedDateEvents.length > 0
                            ? `${selectedDateEvents.length} event${selectedDateEvents.length > 1 ? "s" : ""} scheduled`
                            : "No events"}
                        </div>
                      </div>
                      {selectedDateEvents.length > 0 && (
                        <span
                          className="badge rounded-pill"
                          style={{
                            background:
                              "linear-gradient(135deg,#16202b,#30465e)",
                            fontSize: "0.72rem",
                          }}
                        >
                          {selectedDateEvents.length}
                        </span>
                      )}
                    </div>

                    {selectedDateEvents.length === 0 ? (
                      <div
                        className="text-center py-4 rounded-3"
                        style={{
                          background: "#fcfaf6",
                          border: "1.5px dashed #dcc3a2",
                        }}
                      >
                        <i
                          className="fas fa-calendar-times mb-2 d-block"
                          style={{ fontSize: "1.8rem", color: "#d7b286" }}
                        />
                        <div className="text-muted small">
                          No events on this day
                        </div>
                        <div
                          className="text-muted"
                          style={{ fontSize: "0.72rem" }}
                        >
                          Fill the form on the right to add one
                        </div>
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {selectedDateEvents.map((ev) => (
                          <div
                            key={ev.id || `${ev.name}-${ev.event_datetime}`}
                            className="em-event-row d-flex align-items-center gap-3 p-2 rounded-3 bg-white border"
                            onClick={() => setSelectedEvent(ev)}
                          >
                            {ev.image_url ? (
                              <img
                                src={ev.image_url}
                                alt={ev.name}
                                className="em-thumb"
                              />
                            ) : (
                              <div className="em-thumb-ph">
                                <i
                                  className="fas fa-image"
                                  style={{
                                    color: "#c59a61",
                                    fontSize: "1.1rem",
                                  }}
                                />
                              </div>
                            )}
                            <div className="flex-grow-1 overflow-hidden">
                              <div
                                className="fw-semibold text-truncate"
                                style={{ fontSize: "0.88rem" }}
                              >
                                {ev.name}
                              </div>
                              <div
                                className="text-muted"
                                style={{ fontSize: "0.72rem" }}
                              >
                                <i
                                  className="fas fa-clock me-1"
                                  style={{ color: "#a97234" }}
                                />
                                {formatDisplay(ev.event_datetime)}
                              </div>
                            </div>
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center"
                              style={{
                                width: 28,
                                height: 28,
                                background: "#f6ecdf",
                                flexShrink: 0,
                              }}
                            >
                              <i
                                className="fas fa-chevron-right"
                                style={{
                                  fontSize: "0.65rem",
                                  color: "#a97234",
                                }}
                              />
                            </div>

                            {isAdmin && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger ms-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteEvent(ev);
                                }}
                                disabled={deletingEventId === ev.id}
                                title="Delete event"
                              >
                                {deletingEventId === ev.id ? (
                                  <i className="fas fa-spinner fa-spin" />
                                ) : (
                                  <i className="fas fa-trash" />
                                )}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <div
                      className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                      style={{
                        width: 60,
                        height: 60,
                        background: "linear-gradient(135deg,#fff8ee,#f3e2c7)",
                      }}
                    >
                      <i
                        className="fas fa-hand-pointer"
                        style={{ fontSize: "1.4rem", color: "#a97234" }}
                      />
                    </div>
                    <div className="text-muted small fw-semibold">
                      Select a date
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                      Click any day to see or manage its events
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ══════ RIGHT — FORM + STATS ══════ */}
          <div className="col-xl-5 col-lg-12 d-flex flex-column gap-4">
            {/* Stats row */}
            {events.length > 0 && (
              <div className="d-flex gap-3">
                <div className="em-stat em-gradient-purple">
                  <div className="em-stat-num">{events.length}</div>
                  <div className="em-stat-label">Total Events</div>
                </div>
                <div className="em-stat em-gradient-green">
                  <div className="em-stat-num">
                    {
                      new Set(
                        events.map((e) => e?.event_datetime?.slice(0, 10)),
                      ).size
                    }
                  </div>
                  <div className="em-stat-label">Days Booked</div>
                </div>
                <div className="em-stat em-gradient-orange">
                  <div className="em-stat-num">
                    {
                      events.filter(
                        (e) => new Date(e.event_datetime) >= new Date(),
                      ).length
                    }
                  </div>
                  <div className="em-stat-label">Upcoming</div>
                </div>
              </div>
            )}

            {/* Form card */}
            <div
              className="card border-0 shadow-sm flex-grow-1"
              style={{ borderRadius: 18 }}
            >
              {/* Form header */}
              <div
                className="em-gradient-blue px-4 py-3"
                style={{ borderRadius: "18px 18px 0 0" }}
              >
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: 36,
                      height: 36,
                      background: "rgba(255,255,255,0.2)",
                    }}
                  >
                    <i
                      className="fas fa-calendar-plus text-white"
                      style={{ fontSize: "0.95rem" }}
                    />
                  </div>
                  <div>
                    <div
                      className="text-white fw-bold"
                      style={{ fontSize: "1rem" }}
                    >
                      Add New Event
                    </div>
                    <div
                      className="text-white"
                      style={{ fontSize: "0.7rem", opacity: 0.75 }}
                    >
                      {isAdmin ? "Fill in the details below" : "Admin only"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-body px-4 py-4">
                {!isAdmin ? (
                  <div
                    className="d-flex flex-column align-items-center justify-content-center py-5 text-center"
                  >
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                      style={{
                        width: 64,
                        height: 64,
                        background: "linear-gradient(135deg,#f3e2c7,#f9eee0)",
                      }}
                    >
                      <i
                        className="fas fa-lock"
                        style={{ fontSize: "1.8rem", color: "#a97234" }}
                      />
                    </div>
                    <div className="fw-semibold text-dark mb-2">
                      Admin Only
                    </div>
                    <div className="text-muted small" style={{ fontSize: "0.85rem" }}>
                      Only administrators can create new events. Contact an admin to add events.
                    </div>
                  </div>
                ) : (
                  <>
                    {successMsg && (
                      <div
                        className="d-flex align-items-center gap-2 mb-3 p-3 rounded-3"
                        style={{
                          background: "#edf8f3",
                          border: "1px solid #9fd4c2",
                          color: "#1f7f61",
                          fontSize: "0.85rem",
                        }}
                      >
                        <i className="fas fa-check-circle" />
                        <span>{successMsg}</span>
                      </div>
                    )}

                    <form onSubmit={handleAddEvent} noValidate>
                  {/* Event Name */}
                  <div className="mb-3">
                    <label
                      className="form-label fw-semibold text-secondary mb-1"
                      style={{
                        fontSize: "0.8rem",
                        letterSpacing: "0.03em",
                        textTransform: "uppercase",
                      }}
                    >
                      Event Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      className={`form-control em-form-control rounded-3 ${errors.name ? "is-invalid" : ""}`}
                      placeholder="e.g. Annual Charity Gala"
                      value={formData.name}
                      onChange={handleInputChange}
                      maxLength={100}
                      style={{ fontSize: "0.9rem", padding: "10px 14px" }}
                    />
                    {errors.name && (
                      <div className="invalid-feedback">{errors.name}</div>
                    )}
                  </div>

                  {/* Timing */}
                  <div className="mb-3">
                    <label
                      className="form-label fw-semibold text-secondary mb-1"
                      style={{
                        fontSize: "0.8rem",
                        letterSpacing: "0.03em",
                        textTransform: "uppercase",
                      }}
                    >
                      Date &amp; Time <span className="text-danger">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      name="timing"
                      className={`form-control em-form-control rounded-3 ${errors.timing ? "is-invalid" : ""}`}
                      value={formData.timing}
                      onChange={handleInputChange}
                      style={{ fontSize: "0.9rem", padding: "10px 14px" }}
                    />
                    {errors.timing && (
                      <div className="invalid-feedback">{errors.timing}</div>
                    )}
                  </div>

                  {/* Description */}
                  <div className="mb-3">
                    <label
                      className="form-label fw-semibold text-secondary mb-1"
                      style={{
                        fontSize: "0.8rem",
                        letterSpacing: "0.03em",
                        textTransform: "uppercase",
                      }}
                    >
                      Description <span className="text-danger">*</span>
                    </label>
                    <textarea
                      name="description"
                      className={`form-control em-form-control rounded-3 ${errors.description ? "is-invalid" : ""}`}
                      rows={3}
                      placeholder="Describe the event..."
                      value={formData.description}
                      onChange={handleInputChange}
                      maxLength={500}
                      style={{
                        fontSize: "0.9rem",
                        padding: "10px 14px",
                        resize: "none",
                      }}
                    />
                    <div className="d-flex justify-content-between mt-1">
                      {errors.description ? (
                        <div
                          className="invalid-feedback d-block"
                          style={{ fontSize: "0.78rem" }}
                        >
                          {errors.description}
                        </div>
                      ) : (
                        <span />
                      )}
                      <small
                        className="text-muted"
                        style={{ fontSize: "0.72rem" }}
                      >
                        {formData.description.length}/500
                      </small>
                    </div>
                  </div>

                  {/* Image Upload */}
                  <div className="mb-4">
                    <label
                      className="form-label fw-semibold text-secondary mb-1"
                      style={{
                        fontSize: "0.8rem",
                        letterSpacing: "0.03em",
                        textTransform: "uppercase",
                      }}
                    >
                      Event Image <span className="text-danger">*</span>
                    </label>
                    <input
                      type="file"
                      ref={fileInputRef}
                      className={`form-control em-form-control rounded-3 ${errors.image ? "is-invalid" : ""}`}
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ fontSize: "0.88rem", padding: "9px 14px" }}
                    />
                    {errors.image && (
                      <div className="invalid-feedback">{errors.image}</div>
                    )}
                    {imagePreview && (
                      <div className="mt-2 position-relative">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="em-preview-img"
                        />
                        <button
                          type="button"
                          className="btn btn-danger em-remove-btn"
                          onClick={() => {
                            URL.revokeObjectURL(imagePreview);
                            setImagePreview(null);
                            setFormData((p) => ({ ...p, image: null }));
                            if (fileInputRef.current)
                              fileInputRef.current.value = "";
                          }}
                          title="Remove image"
                        >
                          <i className="fas fa-times" />
                        </button>
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn w-100 py-2 em-submit-btn text-white"
                  >
                    <i className="fas fa-calendar-plus me-2" />
                    {loading ? "Adding..." : "Add Event to Calendar"}
                  </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════ EVENT DETAIL MODAL ══════ */}
      {selectedEvent && (
        <div
          className="modal fade show d-block"
          style={{
            background: "rgba(15,23,42,0.6)",
            backdropFilter: "blur(4px)",
          }}
          onClick={() => setSelectedEvent(null)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: 480 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-content border-0 shadow-lg overflow-hidden"
              style={{ borderRadius: 20 }}
            >
              {/* Hero image */}
              {(selectedEvent.image_url || selectedEvent.imageUrl) && (
                <div style={{ position: "relative", height: 220 }}>
                  <img
                    src={selectedEvent.image_url || selectedEvent.imageUrl}
                    alt={selectedEvent.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 60%)",
                    }}
                  />
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    style={{ position: "absolute", top: 14, right: 14 }}
                    onClick={() => setSelectedEvent(null)}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: 14,
                      left: 18,
                      right: 18,
                    }}
                  >
                    <h5
                      className="text-white fw-bold mb-0"
                      style={{ textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}
                    >
                      {selectedEvent.name}
                    </h5>
                  </div>
                </div>
              )}

              <div className="modal-body px-4 py-4">
                {!selectedEvent.image_url && !selectedEvent.imageUrl && (
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h5 className="fw-bold mb-0">{selectedEvent.name}</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setSelectedEvent(null)}
                    />
                  </div>
                )}

                {/* Date & Time */}
                <div
                  className="d-flex align-items-center gap-3 p-3 rounded-3 mb-3"
                  style={{ background: "#fcf6ee" }}
                >
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: 38,
                      height: 38,
                      background: "linear-gradient(135deg,#16202b,#30465e)",
                      flexShrink: 0,
                    }}
                  >
                    <i
                      className="fas fa-clock text-white"
                      style={{ fontSize: "0.85rem" }}
                    />
                  </div>
                  <div>
                    <div
                      className="text-muted"
                      style={{
                        fontSize: "0.7rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Date &amp; Time
                    </div>
                    <div
                      className="fw-semibold text-dark"
                      style={{ fontSize: "0.9rem" }}
                    >
                      {formatDisplay(selectedEvent.event_datetime)}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div
                  className="d-flex gap-3 p-3 rounded-3"
                  style={{ background: "#fcfaf6" }}
                >
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: 38,
                      height: 38,
                      background: "linear-gradient(135deg,#bf8a4a,#d9a66a)",
                      flexShrink: 0,
                    }}
                  >
                    <i
                      className="fas fa-align-left text-white"
                      style={{ fontSize: "0.85rem" }}
                    />
                  </div>
                  <div>
                    <div
                      className="text-muted mb-1"
                      style={{
                        fontSize: "0.7rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Description
                    </div>
                    <div
                      className="text-secondary"
                      style={{ fontSize: "0.88rem", lineHeight: 1.6 }}
                    >
                      {selectedEvent.description}
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer border-0 px-4 pb-4 pt-0">
                {isAdmin && (
                  <button
                    className="btn btn-outline-danger fw-semibold py-2"
                    onClick={() => handleDeleteEvent(selectedEvent)}
                    disabled={deletingEventId === selectedEvent?.id}
                  >
                    {deletingEventId === selectedEvent?.id ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-2" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-trash me-2" />
                        Delete Event
                      </>
                    )}
                  </button>
                )}
                <button
                  className={`btn text-white fw-semibold py-2 em-submit-btn ${
                    isAdmin ? "px-4" : "w-100"
                  }`}
                  onClick={() => setSelectedEvent(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventManagement;
